import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { NS, PROFILE_KEYS } from '../../i18n/constants/keys';
import { Button } from '../../ui';
import {
  ICalendarConnection,
  ICalendarStatus,
  disconnectCalendar,
  getCalendarStatus,
  startCalendarConnect,
} from '../../utils/api_service/SmartInterview';

/**
 * Lets an interview host connect their own Microsoft 365 or Google calendar.
 *
 * Once connected, interview invitations are created as real events in that
 * mailbox, so the candidate receives a genuine meeting request organised by
 * the host rather than an email from HiAcuity. Without a connection nothing
 * breaks -- the invitation falls back to the emailed version -- so this is
 * presented as an upgrade, not a requirement.
 */
export const CalendarIntegration: React.FC = () => {
  const { t } = useTranslation(NS.profile);
  const [status, setStatus] = useState<ICalendarStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [busyProvider, setBusyProvider] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const response = await getCalendarStatus();
      if (!response.ok) throw new Error(`Status ${response.status}`);
      setStatus(await response.json());
    } catch {
      setStatus({ availableProviders: [], connections: [] });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // The consent round trip returns here as ?calendar=connected|error, since the
  // provider redirects the browser rather than answering our request.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('calendar');
    if (!outcome) return;

    if (outcome === 'connected') {
      toast.success(t(PROFILE_KEYS.sections.calendar.connected));
    } else {
      const reason = params.get('reason');
      toast.error(
        reason === 'denied'
          ? t(PROFILE_KEYS.sections.calendar.consentDeclined)
          : t(PROFILE_KEYS.sections.calendar.connectFailed),
      );
    }
    // Drop the query so a refresh does not re-toast.
    window.history.replaceState({}, '', window.location.pathname);
    loadStatus();
  }, [t, loadStatus]);

  const connect = async (provider: 'microsoft' | 'google') => {
    setBusyProvider(provider);
    try {
      const response = await startCalendarConnect(provider);
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const { authorizationUrl } = await response.json();
      // Full navigation, not a fetch: consent happens on the provider's domain.
      window.location.href = authorizationUrl;
    } catch {
      toast.error(t(PROFILE_KEYS.sections.calendar.connectFailed));
      setBusyProvider(null);
    }
  };

  const disconnect = async (provider: 'microsoft' | 'google') => {
    setBusyProvider(provider);
    try {
      const response = await disconnectCalendar(provider);
      if (!response.ok) throw new Error(`Status ${response.status}`);
      toast.success(t(PROFILE_KEYS.sections.calendar.disconnected));
      await loadStatus();
    } catch {
      toast.error(t(PROFILE_KEYS.sections.calendar.disconnectFailed));
    } finally {
      setBusyProvider(null);
    }
  };

  if (isLoading) {
    return (
      <p className='text-sm text-gray-500'>
        { t(PROFILE_KEYS.sections.calendar.loading) }
      </p>
    );
  }

  const connectionFor = (provider: string): ICalendarConnection | undefined =>
    status?.connections.find((c) => c.provider === provider);

  if (!status?.availableProviders.length) {
    return (
      <p className='text-sm text-gray-500'>
        { t(PROFILE_KEYS.sections.calendar.unavailable) }
      </p>
    );
  }

  return (
    <div className='space-y-4'>
      <p className='text-sm text-gray-600'>
        { t(PROFILE_KEYS.sections.calendar.explanation) }
      </p>

      { status.availableProviders.map(({ provider, label }) => {
        const connection = connectionFor(provider);
        const isBusy = busyProvider === provider;

        return (
          <div
              key={provider}
              className='flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between'
          >
            <div className='min-w-0'>
              <p className='font-medium text-gray-900'>{ label }</p>
              { connection ? (
                <p className='truncate text-sm text-gray-500'>
                  { connection.accountEmail }
                </p>
              ) : (
                <p className='text-sm text-gray-500'>
                  { t(PROFILE_KEYS.sections.calendar.notConnected) }
                </p>
              ) }
              { connection?.needsReauth && (
                <p className='mt-1 text-sm text-amber-700'>
                  { t(PROFILE_KEYS.sections.calendar.needsReauth) }
                </p>
              ) }
            </div>

            <div className='flex flex-shrink-0 items-center gap-2'>
              { connection && !connection.needsReauth ? (
                <Button
                    type='button'
                    variant='secondary'
                    disabled={isBusy}
                    onClick={() => disconnect(provider)}
                    label={t(PROFILE_KEYS.sections.calendar.disconnect)}
                />
              ) : (
                <Button
                    type='button'
                    variant='primary'
                    disabled={isBusy}
                    onClick={() => connect(provider)}
                    label={
                      connection?.needsReauth
                        ? t(PROFILE_KEYS.sections.calendar.reconnect)
                        : t(PROFILE_KEYS.sections.calendar.connect)
                    }
                />
              ) }
            </div>
          </div>
        );
      }) }
    </div>
  );
};
